import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { FaLaptopCode, FaCloud, FaShoppingCart, FaNetworkWired, FaShieldAlt, FaCogs } from "react-icons/fa";

const Services = () => {
  const services = [
    {
      icon: <FaLaptopCode className="text-5xl text-blue-500" />,
      title: "IT Consulting & Support",
      description: "Not sure where to start with your IT? Our expert consultants assess your current setup, identify gaps, and create a clear roadmap tailored to your business goals. From day-to-day helpdesk support to long-term IT strategy, DevOpsMitra is your dedicated technology advisor.",
      features: [
        "IT infrastructure assessment",
        "Technology roadmap planning",
        "Helpdesk & remote support",
        "Vendor management"
      ]
    },
    {
      icon: <FaCloud className="text-5xl text-blue-500" />,
      title: "Cloud Solutions (AWS / Azure / GCP)",
      description: "Move your business to the cloud with confidence. DevOpsMitra designs, migrates, and manages cloud environments on AWS, Microsoft Azure, and Google Cloud Platform. We help SMBs get enterprise-grade cloud power at a cost that makes sense.",
      features: [
        "Cloud readiness assessment",
        "Cloud migration & setup",
        "Cost optimization & monitoring",
        "Ongoing cloud management"
      ]
    },
    {
      icon: <FaShoppingCart className="text-5xl text-blue-500" />,
      title: "Hardware & Software Procurement",
      description: "Stop overpaying for IT equipment and licenses. DevOpsMitra leverages its supplier network to source laptops, servers, networking gear, and software licenses at competitive prices — with genuine products and warranty support.",
      features: [
        "Laptops, desktops & servers",
        "Networking hardware (routers, switches, firewalls)",
        "Licensed software & subscriptions",
        "AMC (Annual Maintenance Contracts)"
      ]
    },
    {
      icon: <FaNetworkWired className="text-5xl text-blue-500" />,
      title: "Networking & Infrastructure",
      description: "A fast, reliable network is the backbone of your business. DevOpsMitra designs and deploys wired and wireless networks, sets up data centers, and ensures your infrastructure is built to handle today's demands and tomorrow's growth.",
      features: [
        "LAN / WAN setup & configuration",
        "Wi-Fi deployment & optimization",
        "Server room & data center setup",
        "Infrastructure health monitoring"
      ]
    },
    {
      icon: <FaShieldAlt className="text-5xl text-blue-500" />,
      title: "Cybersecurity",
      description: "Cyber threats don't target only big companies — SMBs are increasingly the #1 target. DevOpsMitra protects your business with layered security solutions, from firewalls and endpoint protection to employee awareness training and compliance support.",
      features: [
        "Firewall & endpoint security",
        "Vulnerability assessments",
        "Data backup & disaster recovery",
        "Security policy & compliance consulting"
      ]
    },
    {
      icon: <FaCogs className="text-5xl text-blue-500" />,
      title: "DevOps & Automation Services",
      description: "Speed up your software delivery and reduce manual effort with DevOps best practices. DevOpsMitra helps businesses set up CI/CD pipelines, automate repetitive IT tasks, and adopt modern tools that save time and reduce errors.",
      features: [
        "CI/CD pipeline setup (Jenkins, GitHub Actions)",
        "Infrastructure as Code (Terraform, Ansible)",
        "Container management (Docker, Kubernetes)",
        "Process automation & monitoring"
      ]
    }
  ];

  return (
    <div className="pt-20">
      {/* Header Section */}
      <section className="section-padding bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">Our Services</h1>
            <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto">
              Comprehensive IT solutions designed for small and medium businesses that want to grow without limits.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-slate-50 p-8 rounded-xl border border-slate-200 hover:border-blue-500/50 transition-colors duration-300"
                data-testid={`service-detail-${index}`}
              >
                <div className="mb-4">{service.icon}</div>
                <h3 className="text-2xl font-bold mb-4 text-slate-900">{service.title}</h3>
                <p className="text-slate-600 mb-6">{service.description}</p>
                <div className="space-y-2">
                  <p className="font-semibold text-slate-900 mb-3">What's included:</p>
                  <ul className="space-y-2">
                    {service.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-slate-600">
                        <span className="text-blue-500 mt-1">•</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-slate-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-slate-900">
              Not Sure Which Service You Need?
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Talk to our team for a free 30-minute consultation. We'll understand your business and recommend exactly what you need — nothing more, nothing less.
            </p>
            <Link to="/contact">
              <Button 
                data-testid="get-advice-btn"
                className="bg-slate-900 text-white hover:bg-slate-800 shadow-lg hover:shadow-xl transition-all duration-300 rounded-lg px-8 py-6 text-lg font-semibold"
              >
                Get Free Advice →
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
